import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box } from '@amzn/awsui-components-console';
import StreamingCursor from './StreamingCursor';

interface StreamingMessageProps {
  /** Accumulated text so far (grows with each delta). */
  text: string;
  /** Whether the stream is still active. Shows the blinking cursor. */
  isStreaming: boolean;
}

/**
 * Renders streaming text as markdown with a blinking cursor.
 * Re-renders efficiently on each delta chunk.
 */
const StreamingMessage: React.FC<StreamingMessageProps> = ({ text, isStreaming }) => {
  const markdownComponents = useMemo(
    () => ({
      p: ({ children }: any) => <div style={{ marginBottom: '8px' }}>{children}</div>,
      strong: ({ children }: any) => <strong>{children}</strong>,
      em: ({ children }: any) => <em>{children}</em>,
      code: ({ children, ...props }: any) => {
        const isInline = !props.node?.position;
        return isInline ? (
          <code
            style={{
              backgroundColor: '#f2f3f3',
              padding: '2px 4px',
              borderRadius: '3px',
              fontFamily: 'Monaco, Menlo, monospace',
              fontSize: '0.9em',
            }}
          >
            {children}
          </code>
        ) : (
          <pre
            style={{
              backgroundColor: '#f2f3f3',
              padding: '12px',
              borderRadius: '4px',
              overflow: 'auto',
              fontFamily: 'Monaco, Menlo, monospace',
              fontSize: '0.9em',
            }}
          >
            <code>{children}</code>
          </pre>
        );
      },
      ul: ({ children }: any) => (
        <ul style={{ marginLeft: '20px', marginBottom: '8px' }}>{children}</ul>
      ),
      ol: ({ children }: any) => (
        <ol style={{ marginLeft: '20px', marginBottom: '8px' }}>{children}</ol>
      ),
      li: ({ children }: any) => <li style={{ marginBottom: '4px' }}>{children}</li>,
      a: ({ href, children }: any) => (
        <a href={href} style={{ color: '#0073bb' }}>
          {children}
        </a>
      ),
      h1: ({ children }: any) => (
        <h1 style={{ fontSize: '1.5em', fontWeight: 'bold', marginBottom: '8px' }}>{children}</h1>
      ),
      h2: ({ children }: any) => (
        <h2 style={{ fontSize: '1.3em', fontWeight: 'bold', marginBottom: '8px' }}>{children}</h2>
      ),
      h3: ({ children }: any) => (
        <h3 style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '8px' }}>{children}</h3>
      ),
    }),
    [],
  );

  if (!text && isStreaming) {
    return (
      <Box color="text-status-inactive">
        <StreamingCursor />
      </Box>
    );
  }

  return (
    <div>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{text}</ReactMarkdown>
      {isStreaming && <StreamingCursor />}
    </div>
  );
};

export default StreamingMessage;
